import TrialReminder from "@/components/TrialReminder";
import { Suspense, lazy } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, Link, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import { Toaster } from "@/components/ui/sonner";
import { useSeoTitle } from "@/lib/useSeoTitle";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Worlds from "@/pages/Worlds";
import Tip from "@/pages/Tip";
import TipSuccess from "@/pages/TipSuccess";
import ForgotPassword from "@/pages/ForgotPassword";
import Support from "@/pages/Support";
import TrialPlay from "@/pages/TrialPlay";
import HowToPlay from "@/pages/HowToPlay";

// SEO landing pages — high-intent keyword targets with real, crawlable copy
// under /ai-novel-game, /ai-dnd-game, /ai-dungeon-alternative,
// /novelai-alternative, /choose-your-own-adventure-ai, /ai-fantasy-novel-writer
import AiNovelGame from "@/pages/seo/AiNovelGame";
import AiDndGame from "@/pages/seo/AiDndGame";
import AiDungeonAlternative from "@/pages/seo/AiDungeonAlternative";
import NovelAiAlternative from "@/pages/seo/NovelAiAlternative";
import ChooseYourOwnAdventureAi from "@/pages/seo/ChooseYourOwnAdventureAi";
import AiFantasyNovelWriter from "@/pages/seo/AiFantasyNovelWriter";
import WorldDetail from "@/pages/WorldDetail";

// Trust / policy pages — required for domain trust, GEO (Generative Engine
// Optimisation) signals, and app-store style eligibility checks.
import About from "@/pages/policy/About";
import Privacy from "@/pages/policy/Privacy";
import Terms from "@/pages/policy/Terms";
import ContentPolicy from "@/pages/policy/ContentPolicy";
import Changelog from "@/pages/policy/Changelog";
import ReleaseNotesGate from "@/components/ReleaseNotesGate";

const Dashboard = lazy(() => import("@/pages/Dashboard"));
const CharacterCreate = lazy(() => import("@/pages/CharacterCreate"));
const CampaignCreate = lazy(() => import("@/pages/CampaignCreate"));
const Play = lazy(() => import("@/pages/Play"));
const Admin = lazy(() => import("@/pages/Admin"));
const Profile = lazy(() => import("@/pages/Profile"));
const Leaderboards = lazy(() => import("@/pages/Leaderboards"));

function RouteFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center text-amber-300/70 font-display text-2xl">
      Lighting the candles...
    </div>
  );
}

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-amber-300/70 font-display text-2xl">
        Lighting the candles...
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function NotFound() {
  const location = useLocation();
  useSeoTitle({
    title: "Page not found | TalesTalesTales",
    description: "The requested page could not be found on TalesTalesTales.",
    path: location.pathname,
    noindex: true,
  });

  return (
    <div className="min-h-screen bg-[#0f0d11] text-amber-50">
      <main className="max-w-3xl mx-auto px-6 py-24 text-center">
        <p className="font-ui text-[10px] uppercase tracking-[0.4em] text-amber-400/80 mb-4">404</p>
        <h1 className="font-display text-4xl sm:text-5xl mb-4">That path does not exist</h1>
        <p className="font-body text-amber-100/75 leading-relaxed max-w-xl mx-auto">
          The page you asked for could not be found. Return to the homepage, browse worlds, or start a free trial.
        </p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <Link to="/" className="parchment gold-border rounded-full px-4 py-2 font-ui text-xs uppercase tracking-widest text-amber-200">
            Home
          </Link>
          <Link to="/worlds" className="parchment gold-border rounded-full px-4 py-2 font-ui text-xs uppercase tracking-widest text-amber-200">
            Worlds
          </Link>
          <Link to="/trial" className="parchment gold-border rounded-full px-4 py-2 font-ui text-xs uppercase tracking-widest text-amber-200">
            Free trial
          </Link>
        </div>
      </main>
    </div>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <ReleaseNotesGate />
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot" element={<ForgotPassword />} />
            <Route path="/support" element={<Support />} />
            <Route path="/profile" element={<Protected><Suspense fallback={<RouteFallback />}><Profile /></Suspense></Protected>} />
            <Route path="/leaderboards" element={<Suspense fallback={<RouteFallback />}><Leaderboards /></Suspense>} />
            <Route path="/worlds" element={<Worlds />} />
            <Route path="/trial" element={<TrialPlay />} />
            <Route path="/how-to-play" element={<HowToPlay />} />
            {/* Pricing — canonical `/pricing` URL. Old `/tip` route kept
                permanently redirected so previously-shared links still work
                and Google can pass link equity to the canonical URL. */}
            <Route path="/pricing" element={<Tip />} />
            <Route path="/pricing/success" element={<TipSuccess />} />
            <Route path="/tip" element={<Navigate to="/pricing" replace />} />
            <Route path="/tip/success" element={<Navigate to="/pricing/success" replace />} />
            <Route path="/admin" element={<Protected><Suspense fallback={<RouteFallback />}><Admin /></Suspense></Protected>} />
            <Route path="/dashboard" element={<Protected><Suspense fallback={<RouteFallback />}><Dashboard /></Suspense></Protected>} />
            <Route path="/character/new" element={<Protected><Suspense fallback={<RouteFallback />}><CharacterCreate /></Suspense></Protected>} />
            <Route path="/campaign/new" element={<Protected><Suspense fallback={<RouteFallback />}><CampaignCreate /></Suspense></Protected>} />
            <Route path="/play/:cid" element={<Protected><Suspense fallback={<RouteFallback />}><Play /></Suspense></Protected>} />
            {/* SEO landing pages — public, no auth. Each has ~600-900 words
                of unique visible copy plus internal links back to the funnel. */}
            <Route path="/ai-novel-game" element={<AiNovelGame />} />
            <Route path="/ai-dnd-game" element={<AiDndGame />} />
            <Route path="/ai-dungeon-alternative" element={<AiDungeonAlternative />} />
            <Route path="/novelai-alternative" element={<NovelAiAlternative />} />
            <Route path="/choose-your-own-adventure-ai" element={<ChooseYourOwnAdventureAi />} />
            <Route path="/ai-fantasy-novel-writer" element={<AiFantasyNovelWriter />} />
            <Route path="/worlds/:slug" element={<WorldDetail />} />
            {/* Trust / policy pages — public, no auth. */}
            <Route path="/about"          element={<About />} />
            <Route path="/privacy"        element={<Privacy />} />
            <Route path="/terms"          element={<Terms />} />
            <Route path="/content-policy" element={<ContentPolicy />} />
            <Route path="/changelog"      element={<Changelog />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
          <TrialReminder />
        </BrowserRouter>
        <Toaster position="top-center" />
      </AuthProvider>
    </div>
  );
}

export default App;
